import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import { createBrowserRouter, Route, RouterProvider, Routes } from "react-router";
import colors from "tailwindcss/colors";
import { APIContextProvider } from "./api/APIContext";
import { AuthMiddleware } from "./auth/AuthMiddleware";
import { AuthProvider } from "./auth/AuthProvider";
import { ModalProvider } from "./components/layout/ModalProvider";
import { RefreshController } from "./components/RefreshController";
import { AssessorsPage } from "./pages/AssessorsPage";
import { BrowseAndFilterPage } from "./pages/BrowseAndFilterPage";
import { CertificatesPage } from "./pages/CertificatesPage";
import { CompaniesDetailsPage } from "./pages/Companies/CompaniesDetailsPage";
import { CompaniesListPage } from "./pages/Companies/CompaniesListPage";
import { CompanySettingsPage } from "./pages/Companies/CompanySettingsPage";
import { DashboardsOverviewPage } from "./pages/Dashboards/DashboardsOverviewPage";
import { DashboardsWorkstationInspections } from "./pages/Dashboards/DashboardsWorkstationInspections";
import { ExamDetailsPage } from "./pages/ExamDetailsPage";
import { ExpiredPage } from "./pages/ExpiredPage";
import { ForbiddenPage } from "./pages/ForbiddenPage";
import { ImportPage } from "./pages/ImportPage";
import { InspectionDetailsPage } from "./pages/InspectionDetailsPage";
import { InspectionQuestionsPage } from "./pages/InspectionQuestionsPage";
import { OverviewPage } from "./pages/OverviewPage";
import { QuestionSetsPage } from "./pages/QuestionSetsPage";
import { SettingsAssessorsPage } from "./pages/Settings/SettingsAssessorsPage";
import { SettingsAttachmentsPage } from "./pages/Settings/SettingsAttachmentsPage";
import { SettingsCertificationTypesPage } from "./pages/Settings/SettingsCertificationTypesPage";
import { SettingsChecklistEditorPage } from "./pages/Settings/SettingsChecklistEditorPage";
import { SettingsChecklistsPage } from "./pages/Settings/SettingsChecklistsPage";
import { SettingsEmailImportPage } from "./pages/Settings/SettingsEmailImportPage";
import { SettingsLiftingCategoryEditorPage } from "./pages/Settings/SettingsLiftingCategoryEditorPage";
import { SettingsOverviewPage } from "./pages/Settings/SettingsOverviewPage";
import { SettingsUserDetailsPage } from "./pages/Settings/SettingsUserDetailsPage";
import { SettingsUsersPage } from "./pages/Settings/SettingsUsersPage";
import initSentry from "./sentry";
import { generateDynamicColorSchemeCSS } from "./util/colorScheme";

initSentry();

// A data router (rather than `<BrowserRouter>`) is required for `useBlocker`, which is used to warn
// about unsaved changes. The single catch-all route keeps the flat `<Routes>` declaration below intact.
const router = createBrowserRouter([
  {
    path: "*",
    element: (
      <RefreshController>
        <AuthMiddleware>
          <APIContextProvider>
            <ModalProvider>
              <Routes>
                <Route path={"/"} element={<OverviewPage />} />
                <Route path={"/browse"} element={<BrowseAndFilterPage />} />
                <Route path={"/exams/:examId"} element={<ExamDetailsPage />} />
                <Route path={"/inspections/:inspectionId"} element={<InspectionDetailsPage />} />
                <Route
                  path={"/inspections/:inspectionId/questions"}
                  element={<InspectionQuestionsPage />}
                />
                <Route path={"/companies"} element={<CompaniesListPage />} />
                <Route path={"/companies/:companyId"} element={<CompaniesDetailsPage />} />
                <Route path={"/companies/:companyId/settings"} element={<CompanySettingsPage />} />
                <Route path={"/question-sets"} element={<QuestionSetsPage />} />
                <Route path={"/export-import"} element={<ImportPage />} />
                <Route path={"/assessors"} element={<AssessorsPage />} />
                <Route path={"/certificates"} element={<CertificatesPage />} />
                <Route path={"/forbidden"} element={<ForbiddenPage />} />
                <Route path={"/expired"} element={<ExpiredPage />} />
                <Route path={"/settings"} element={<SettingsOverviewPage />} />
                <Route path={"/settings/users"} element={<SettingsUsersPage />} />
                <Route path={"/settings/users/:userId"} element={<SettingsUserDetailsPage />} />
                <Route path={"/settings/assessors"} element={<SettingsAssessorsPage />} />
                <Route path={"/settings/email-import"} element={<SettingsEmailImportPage />} />
                <Route
                  path={"/settings/certification-types"}
                  element={<SettingsCertificationTypesPage />}
                />
                <Route path={"/settings/attachments"} element={<SettingsAttachmentsPage />} />
                <Route path={"/settings/checklists"} element={<SettingsChecklistsPage />} />
                <Route
                  path={"/settings/checklists/:checklistId"}
                  element={<SettingsChecklistEditorPage />}
                />
                <Route
                  path={"/settings/hijscategorie-assistent"}
                  element={<SettingsLiftingCategoryEditorPage />}
                />
                <Route path={"/dashboards"} element={<DashboardsOverviewPage />} />
                <Route
                  path={"/dashboards/workstation-inspepctions"}
                  element={<DashboardsWorkstationInspections />}
                />
              </Routes>
            </ModalProvider>
          </APIContextProvider>
        </AuthMiddleware>
      </RefreshController>
    ),
  },
]);

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <AuthProvider>
    <style>{generateDynamicColorSchemeCSS(colors.cyan)}</style>
    <RouterProvider router={router} />
  </AuthProvider>,
);
